import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, ImageBackground, KeyboardAvoidingView, Platform, ScrollView, Pressable, Dimensions, TouchableOpacity, Modal, Alert } from 'react-native';
import PrimaryButton from '../components/PrimaryButton';
import colors from '../theme/colors';
import { formatCPF, formatPhone } from '../utils/masks';
import { useTableContext } from '../context/TableContext';

const { width } = Dimensions.get('window');

export default function LoginScreen({ onNavigate, screenParams }) {
  const { createTable, tables } = useTableContext();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cpf, setCpf] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [tableSelectionMode, setTableSelectionMode] = useState('digitavel'); // 'digitavel', 'automatica', 'selecionada'
  const [selectedTable, setSelectedTable] = useState(null);
  const [isTableModalVisible, setIsTableModalVisible] = useState(false);
  const { isKioskMode = false, kioskFunctions = {} } = screenParams || {};

  // Verificar mesas existentes para sugerir número disponível
  const existingTableNumbers = tables.map(t => t.mesa);
  const suggestedTableNumber = Math.max(...existingTableNumbers, 0) + 1;
  const automaticTableNumber = suggestedTableNumber;

  // Mesas disponíveis para seleção (apenas fechadas)
  const availableTables = tables.filter(t => t.status === 'fechada');
  
  // Mesas ocupadas (abertas ou pausadas)
  const occupiedTables = tables.filter(t => t.status === 'aberta' || t.status === 'pausada');
  const occupiedTableNumbers = occupiedTables.map(t => t.mesa);

  // Capacidade padrão para modos digitável e automático
  const defaultCapacity = 4;

  const handlePhoneChange = (text) => {
    setPhone(formatPhone(text));
  };

  const handleCpfChange = (text) => {
    setCpf(formatCPF(text));
  };

  const handleAccess = () => {
    if (!name || !email || !phone || !cpf) {
      alert('Por favor, preencha todos os campos');
      return;
    }
    
    let finalTableNumber;
    let finalCapacity;
    
    if (tableSelectionMode === 'digitavel') {
      if (!tableNumber) {
        alert('Por favor, digite o número da mesa');
        return;
      }
      
      // Verificar se o número da mesa já está ocupada (aberta ou pausada)
      const tableNumberInt = parseInt(tableNumber);
      const tableOccupied = occupiedTableNumbers.includes(tableNumberInt);
      if (tableOccupied) {
        alert('Este número de mesa já está em uso (mesa aberta ou pausada). Escolha outro número.');
        return;
      }
      finalTableNumber = parseInt(tableNumber);
      finalCapacity = defaultCapacity;
      
    } else if (tableSelectionMode === 'automatica') {
      finalTableNumber = automaticTableNumber;
      finalCapacity = defaultCapacity;
      
    } else if (tableSelectionMode === 'selecionada') {
      if (!selectedTable) {
        alert('Por favor, selecione uma mesa');
        return;
      }
      finalTableNumber = selectedTable.mesa;
      finalCapacity = selectedTable.pessoas;
    }
    
    // Criar nova mesa
    const newTable = createTable({
      mesa: finalTableNumber,
      cliente: name,
      email: email,
      telefone: phone,
      cpf: cpf,
      pessoas: finalCapacity
    });
    
    // Navegar diretamente para venda de produtos
    onNavigate('SellProduct', { tableId: newTable.id });
  };

  const handleSelectTable = (table) => {
    setSelectedTable(table);
    setIsTableModalVisible(false);
  };

  const handleOpenTableModal = () => {
    if (availableTables.length === 0) {
      alert('Nenhuma mesa disponível para seleção no momento.');
      return;
    }
    setIsTableModalVisible(true);
  };

  const handleCloseTableModal = () => {
    setIsTableModalVisible(false);
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.header}>
        <TouchableOpacity onPress={() => onNavigate('Home', { isKioskMode, kioskFunctions })} style={styles.backButtonContainer}>
          <Text style={styles.backButton}>{'<'}</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Abrir Nova Mesa</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
        <View style={styles.formContainer}>
          <Text style={styles.label}>Nome</Text>
          <TextInput
            style={styles.input}
            placeholder="Digite seu nome"
            placeholderTextColor={colors.muted}
            value={name}
            onChangeText={setName}
          />

        <Text style={styles.label}>Email</Text>
        <TextInput
          style={styles.input}
          placeholder="Digite seu email"
          placeholderTextColor={colors.muted}
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
        />

        <View style={styles.rowContainer}>
          <View style={styles.halfWidth}>
            <Text style={styles.label}>Telefone</Text>
            <TextInput
              style={styles.input}
              placeholder="(XX) XXXXX-XXXX"
              placeholderTextColor={colors.muted}
              value={phone}
              onChangeText={handlePhoneChange}
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.halfWidth}>
            <Text style={styles.label}>CPF</Text>
            <TextInput
              style={styles.input}
              placeholder="XXX.XXX.XXX-XX"
              placeholderTextColor={colors.muted}
              value={cpf}
              onChangeText={handleCpfChange}
              keyboardType="numeric"
              maxLength={14}
            />
          </View>
        </View>

        <Text style={styles.label}>Tipo de Mesa</Text>
        <View style={styles.tableModeContainer}>
          <TouchableOpacity
            style={[
              styles.tableModeButton,
              tableSelectionMode === 'digitavel' && styles.tableModeButtonActive
            ]}
            onPress={() => setTableSelectionMode('digitavel')}
          >
            <Text style={[
              styles.tableModeText,
              tableSelectionMode === 'digitavel' && styles.tableModeTextActive
            ]}>
              📝 Digitar Número
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[
              styles.tableModeButton,
              tableSelectionMode === 'automatica' && styles.tableModeButtonActive
            ]}
            onPress={() => setTableSelectionMode('automatica')}
          >
            <Text style={[
              styles.tableModeText,
              tableSelectionMode === 'automatica' && styles.tableModeTextActive
            ]}>
              🎯 Automática
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[
              styles.tableModeButton,
              tableSelectionMode === 'selecionada' && styles.tableModeButtonActive
            ]}
            onPress={() => setTableSelectionMode('selecionada')}
          >
            <Text style={[
              styles.tableModeText,
              tableSelectionMode === 'selecionada' && styles.tableModeTextActive
            ]}>
              📋 Selecionar Mesa
            </Text>
          </TouchableOpacity>
        </View>

        {tableSelectionMode === 'digitavel' && (
          <View>
            <Text style={styles.label}>Número da Mesa</Text>
            <TextInput
              style={styles.input}
              placeholder={`Ex: ${suggestedTableNumber}`}
              placeholderTextColor={colors.muted}
              value={tableNumber}
              onChangeText={setTableNumber}
              keyboardType="numeric"
            />
            <Text style={styles.capacityInfo}>Capacidade padrão: {defaultCapacity} pessoas</Text>
          </View>
        )}

        {tableSelectionMode === 'automatica' && (
          <View style={styles.automaticTableInfo}>
            <Text style={styles.label}>Mesa Automática</Text>
            <View style={styles.automaticTableCard}>
              <Text style={styles.automaticTableNumber}>Mesa {automaticTableNumber}</Text>
              <Text style={styles.automaticTableText}>Número gerado automaticamente</Text>
              <Text style={styles.automaticTableText}>Capacidade: {defaultCapacity} pessoas</Text>
            </View>
          </View>
        )}

        {tableSelectionMode === 'selecionada' && (
          <View>
            <Text style={styles.label}>Mesa Selecionada</Text>
            <TouchableOpacity 
              style={styles.selectedTableButton}
              onPress={handleOpenTableModal}
            >
              {selectedTable ? (
                <View style={styles.selectedTableContent}>
                  <Text style={styles.selectedTableNumber}>Mesa {selectedTable.mesa}</Text>
                  <Text style={styles.selectedTableInfo}>Capacidade: {selectedTable.pessoas} pessoas</Text>
                  <Text style={styles.selectedTableStatus}>Status: {selectedTable.status}</Text>
                </View>
              ) : (
                <View style={styles.selectTablePlaceholder}>
                  <Text style={styles.selectTableText}>Clique para selecionar uma mesa</Text>
                  <Text style={styles.selectTableSubtext}>
                    {availableTables.length > 0 
                      ? `${availableTables.length} mesa(s) disponível(is)` 
                      : 'Nenhuma mesa disponível'
                    }
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
        )}

          <PrimaryButton
            title="Acessar"
            onPress={handleAccess}
            style={styles.button}
          />
        </View>
      </ScrollView>

      {/* Modal de Seleção de Mesa */}
      <Modal
        visible={isTableModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCloseTableModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Selecionar Mesa</Text>
            <Text style={styles.modalSubtitle}>Escolha uma mesa disponível</Text>

            <ScrollView style={styles.tablesGrid} contentContainerStyle={styles.tablesGridContent}>
              {availableTables.map((table) => (
                <TouchableOpacity
                  key={table.id}
                  style={[
                    styles.tableCard,
                    selectedTable?.id === table.id && styles.tableCardSelected
                  ]}
                  onPress={() => handleSelectTable(table)}
                >
                  <Text style={[
                    styles.tableNumber,
                    selectedTable?.id === table.id && styles.tableNumberSelected
                  ]}>
                    Mesa {table.mesa}
                  </Text>
                  <Text style={styles.tableCapacity}>
                    {table.pessoas} pessoas
                  </Text>
                  <Text style={styles.tableStatus}>
                    {table.status}
                  </Text>
                  <Text style={styles.tableCapacityLabel}>
                    Capacidade: {table.pessoas} pessoas
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.modalButtonSecondary]} 
                onPress={handleCloseTableModal}
              >
                <Text style={styles.modalButtonTextSecondary}>Cancelar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background
  },
  header: {
    height: 72,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    flexDirection: 'row'
  },
  backButtonContainer: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center'
  },
  backButton: {
    fontSize: 24,
    color: colors.accent,
    fontWeight: '600'
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
    flex: 1,
    textAlign: 'center'
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 24
  },
  contentContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1
  },
  formContainer: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  },
  rowContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16
  },
  halfWidth: {
    flex: 1
  },
  capacityInfo: {
    fontSize: 12,
    color: colors.muted,
    marginTop: 4,
    fontStyle: 'italic'
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8
  },
  input: {
    borderBottomWidth: 2,
    borderBottomColor: colors.border,
    fontSize: 14,
    paddingVertical: 10,
    marginBottom: 16,
    outlineColor: colors.accent
  },
  button: {
    marginTop: 24,
    marginBottom: 32
  },
  capacityContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
    paddingHorizontal: 0
  },
  capacityButton: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 2,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF'
  },
  capacityButtonActive: {
    backgroundColor: colors.primary
  },
  capacityText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary
  },
  capacityTextActive: {
    color: '#FFFFFF'
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 600,
    maxHeight: '80%'
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 8,
    textAlign: 'center'
  },
  modalSubtitle: {
    fontSize: 14,
    color: colors.muted,
    marginBottom: 20,
    textAlign: 'center'
  },
  tablesGrid: {
    maxHeight: 400,
    marginBottom: 20
  },
  tablesGridContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between'
  },
  tableCard: {
    width: width > 768 ? '23%' : '48%',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    marginBottom: 12
  },
  tableCardOccupied: {
    backgroundColor: '#F5F5F5',
    borderColor: '#E0E0E0',
    opacity: 0.6
  },
  tableCardSelected: {
    borderColor: colors.primary,
    backgroundColor: 'rgba(246, 134, 71, 0.1)'
  },
  tableNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4
  },
  tableNumberOccupied: {
    color: colors.muted
  },
  tableNumberSelected: {
    color: colors.primary
  },
  tableCapacity: {
    fontSize: 13,
    color: colors.muted,
    marginBottom: 8
  },
  tableCapacityOccupied: {
    color: '#999'
  },
  tableStatus: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 4
  },
  tableStatusAvailable: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderWidth: 1,
    borderColor: '#22C55E'
  },
  tableStatusOccupied: {
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    borderWidth: 1,
    borderColor: '#EF4444'
  },
  tableStatusText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.text
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalButtonSecondary: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: colors.border
  },
  modalButtonPrimary: {
    backgroundColor: colors.primary
  },
  modalButtonTextSecondary: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text
  },
  modalButtonTextPrimary: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF'
  },
  confirmModalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 500
  },
  confirmHeader: {
    alignItems: 'center',
    marginBottom: 20
  },
  confirmIcon: {
    fontSize: 48,
    color: '#22C55E',
    marginBottom: 8
  },
  confirmTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.text
  },
  confirmContent: {
    marginBottom: 20
  },
  confirmSection: {
    marginBottom: 16
  },
  confirmSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  confirmRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6
  },
  confirmLabel: {
    fontSize: 14,
    color: colors.muted,
    fontWeight: '600'
  },
  confirmValue: {
    fontSize: 15,
    color: colors.muted,
    fontWeight: '500'
  },
  confirmDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: 16
  },
  // Estilos para seleção de modo de mesa
  tableModeContainer: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 20
  },
  tableModeButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: 'center',
    flex: 1
  },
  tableModeButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  tableModeText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'center'
  },
  tableModeTextActive: {
    color: '#FFFFFF'
  },
  // Estilos para mesa automática
  automaticTableInfo: {
    marginBottom: 20
  },
  automaticTableCard: {
    backgroundColor: colors.primary,
    borderRadius: 12,
    paddingVertical: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.primary
  },
  automaticTableNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4
  },
  automaticTableText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)'
  },
  // Estilos para mesa selecionada
  selectedTableButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    marginBottom: 20
  },
  selectedTableContent: {
    alignItems: 'flex-start'
  },
  selectedTableNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4
  },
  selectedTableInfo: {
    fontSize: 12,
    color: colors.muted,
    marginBottom: 2
  },
  selectedTableStatus: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: '600'
  },
  selectTablePlaceholder: {
    alignItems: 'center'
  },
  selectTableText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.muted,
    marginBottom: 4
  },
  selectTableSubtext: {
    fontSize: 12,
    color: colors.muted
  },
  // Estilos para modal de seleção
  tablesGrid: {
    maxHeight: 300,
    marginBottom: 16
  },
  tablesGridContent: {
    gap: 10
  },
  tableCard: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'flex-start'
  },
  tableCardSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  tableNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4
  },
  tableNumberSelected: {
    color: '#FFFFFF'
  },
  tableCapacity: {
    fontSize: 12,
    color: colors.muted,
    marginBottom: 2
  },
  tableStatus: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: '600'
  },
  tableCapacityLabel: {
    fontSize: 12,
    color: colors.muted,
    fontWeight: '500',
    marginTop: 2
  }
});
